// The curated Model Book chart's chrome (/model_book/:ticker): the legend
// swatches, the per-author layer chips and the chart keys. Lives on the SAME
// element as `lightweight-chart`, which owns the chart and the marks layer;
// this controller only talks to it through its marks API (setVisibleAuthors,
// markOverlays, frameMarksWindow) and the chart's own time scale.
//
// The drawing toolbar (chart-marks-editor) mounts in the
// `[data-model-book-editor]` container inside this element and binds the same
// way — wait for `lightweight-chart:marks-loaded`, then call
// setMarks/addMark/removeMark on the chart controller.
//
// Keys are bound on the focusable chart surface only (never the document),
// so the shared chart controller's stock-list keys and the editor's
// document-level Escape/Delete are untouched; the range math lives in
// utils/model_book_framing.js (docs § Chart framing and keys).

import { Controller } from "@hotwired/stimulus"
import { swatchColor } from "../utils/chart_mark_styles"
import { readTheme } from "../utils/vcp_markup_overlay"
import {
  centreOn,
  endRange,
  homeRange,
  markStartIndices,
  scrollRange,
  stepTarget,
  zoomRange,
  ZOOM_STEP
} from "../utils/model_book_framing"

const EDITABLE = "input, textarea, select"
// Controls inside the surface (the chart's own A/L scale buttons) keep their
// Space/Enter/arrow behaviour.
const CONTROL = "button, a[href], [role=button]"
// An arrow key scrolls a quarter of the screen; with Shift, a whole one.
const SCROLL_FRACTION = 0.25

export default class extends Controller {
  static targets = [ "authorChip", "swatch", "surface" ]

  connect() {
    this.lastStep = null
    this.pendingRange = null
    this.paintSwatches()
    this.boundThemeChanged = () => this.paintSwatches()
    document.addEventListener("theme:changed", this.boundThemeChanged)
  }

  disconnect() {
    if (this.boundThemeChanged) document.removeEventListener("theme:changed", this.boundThemeChanged)
    this.boundThemeChanged = null
  }

  // lightweight-chart:marks-loaded — the layer exists now; re-apply the chips
  // (a chart rebuilt after an error starts with every author visible), and
  // take focus so the keys work at once.
  marksLoaded() {
    this.lastStep = null
    this.pendingRange = null
    this.applyVisibility()
    this.focusChart()
  }

  // Focus the chart surface unless the reader is already somewhere else —
  // typing in a field or on a control they tabbed to.
  focusChart() {
    if (!this.hasSurfaceTarget) return
    const active = document.activeElement
    if (active && active !== document.body && active !== document.documentElement && active !== this.surfaceTarget) return
    this.surfaceTarget.focus({ preventScroll: true })
  }

  // keydown on the chart surface.
  keydown(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    const target = event.target
    if (target && (target.isContentEditable || target.closest?.(EDITABLE) || target.closest?.(CONTROL))) return
    const action = this.actionFor(event)
    if (!action) return
    const context = this.navigationContext()
    if (!context) return
    event.preventDefault()
    action(context)
  }

  // The handler for a key, or null for a key that isn't ours.
  actionFor(event) {
    const shift = event.shiftKey
    switch (event.key) {
    case "ArrowLeft": return ctx => this.apply(ctx, scrollRange(ctx.range, -(shift ? 1 : SCROLL_FRACTION), ctx.bounds))
    case "ArrowRight": return ctx => this.apply(ctx, scrollRange(ctx.range, shift ? 1 : SCROLL_FRACTION, ctx.bounds))
    case "Home": return ctx => this.apply(ctx, homeRange(ctx.range, ctx.bounds))
    case "End": return ctx => this.apply(ctx, endRange(ctx.range, ctx.bounds))
    case "+":
    case "=": return ctx => this.apply(ctx, zoomRange(ctx.range, ZOOM_STEP, ctx.zoomBounds))
    case "-":
    case "_": return ctx => this.apply(ctx, zoomRange(ctx.range, 1 / ZOOM_STEP, ctx.zoomBounds))
    case "0":
    case "f":
    case "F": return () => this.refit()
    case " ":
    case "Spacebar": return ctx => this.stepToMark(ctx, shift ? -1 : 1)
    default: return null
    }
  }

  // What the range math needs from the live chart, or null before it exists.
  navigationContext() {
    const controller = this.chartController()
    const timeScale = controller?.chart?.timeScale?.()
    // A range asked for this frame hasn't been applied yet (the library
    // applies it on the next frame), so a second key press builds on it.
    const range = this.pendingRange || timeScale?.getVisibleLogicalRange?.()
    const bars = controller?.marksWindowData
    if (!timeScale || !range || !Array.isArray(bars) || bars.length === 0) return null
    const options = timeScale.options()
    const bounds = { barCount: bars.length, rightOffset: options.rightOffset || 0 }
    return {
      controller,
      timeScale,
      bars,
      range: { from: range.from, to: range.to },
      bounds,
      zoomBounds: { ...bounds, paneWidth: timeScale.width(), minBarSpacing: options.minBarSpacing }
    }
  }

  apply(ctx, range) {
    this.lastStep = null
    this.setRange(ctx.timeScale, range)
  }

  // Ask for a range and remember it until the next frame, when the library
  // has applied it.
  setRange(timeScale, range) {
    timeScale.setVisibleLogicalRange(range)
    this.pendingRange = range
    const frame = typeof requestAnimationFrame === "function" ? requestAnimationFrame : callback => setTimeout(callback, 16)
    frame(() => { if (this.pendingRange === range) this.pendingRange = null })
  }

  refit() {
    this.lastStep = null
    this.pendingRange = null
    this.chartController()?.frameMarksWindow?.()
  }

  // Space / Shift+Space: centre the next (previous) visible mark's start at
  // the current zoom (utils/model_book_framing.js#stepTarget).
  stepToMark(ctx, direction) {
    const layer = ctx.controller.markOverlays
    const marks = (layer?.marks || []).filter(mark => typeof layer.isMarkVisible !== "function" || layer.isMarkVisible(mark.id))
    const indices = markStartIndices(marks, ctx.bars.map(bar => bar.time))
    const index = stepTarget(indices, ctx.range, this.lastStep?.index, direction)
    if (index === null) return
    this.setRange(ctx.timeScale, centreOn(ctx.range, index, ctx.bounds))
    this.lastStep = { index }
  }

  toggleAuthor(event) {
    const chip = event.currentTarget
    const pressed = chip.getAttribute("aria-pressed") !== "true"
    chip.setAttribute("aria-pressed", pressed ? "true" : "false")
    chip.classList.toggle("btn-primary", pressed)
    chip.classList.toggle("btn-outline-secondary", !pressed)
    this.applyVisibility()
  }

  // The ids of the pressed chips ("" is a mark whose author was removed).
  visibleAuthorIds() {
    return this.authorChipTargets
      .filter(chip => chip.getAttribute("aria-pressed") === "true")
      .map(chip => chip.dataset.authorId || "")
  }

  applyVisibility() {
    const chart = this.chartController()
    if (!chart || typeof chart.setVisibleAuthors !== "function") return
    const allOn = this.authorChipTargets.every(chip => chip.getAttribute("aria-pressed") === "true")
    chart.setVisibleAuthors(allOn ? null : this.visibleAuthorIds())
  }

  paintSwatches() {
    const { dark } = readTheme()
    this.swatchTargets.forEach(swatch => {
      const color = swatchColor(swatch.dataset.kind, dark)
      if (color) swatch.style.backgroundColor = color
    })
  }

  chartController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "lightweight-chart")
  }
}
